import { uuidv4 } from '@/globals/helpers';
import { Component, Element, h, Host, Listen, Prop, Watch, Mixin } from '@stencil/core';
import { animateHeightFactory } from '@/globals/mixins';

/**
 * The `pn-radio-button` is built with a native `input[type="radio"]` in the background.
 * This means you can use native events to listen to the changes.
 *
 * @nativeChange Use the `change` event to listen when the radio state is being changed.
 *
 * @slot - The default slot for adding custom HTML under the label/helpertext. {@since v7.17.0}
 * @slot helpertext - Use this slot to add custom HTML inside of the helpertext element.
 * Only recommended if you need a link without the `tile` prop. {@since v7.17.0}
 * @slot content - When using the `tile` and `expand` props, this slot will be revealed when the input is checked. {@since v7.17.0}
 */
@Component({
  tag: 'pn-radio-button',
  styleUrl: 'pn-radio-button.scss',
})
export class PnRadioButton extends Mixin(animateHeightFactory) {
  private readonly id: string = `pn-radio-${uuidv4()}`;
  private idHelpertext: string = `${this.id}-helpertext`;

  private contentArea: HTMLElement;
  private radioInput: HTMLInputElement;

  @Element() hostElement: HTMLElement;

  /** The radio label */
  @Prop() label?: string;
  /** This adds an optional helpertext under the label. */
  @Prop() helpertext?: string;

  /** This will be emitted on change and input events. */
  @Prop({ reflect: true }) value!: string;
  /** The name of the radio group. */
  @Prop() name?: string;
  /** Check the radio. */
  @Prop({ mutable: true }) checked?: boolean = false;
  /** A unique HTML ID for the radio. */
  @Prop() radioid?: string = this.id;

  /** Set the radio as required. @category State */
  @Prop() required?: boolean = false;
  /** Disable the radio. @category State */
  @Prop() disabled?: boolean = false;
  /** If set to true, color scheme will turn red, indicating that there is an issue or incorrect input related the radio. @category State */
  @Prop() invalid?: boolean = false;

  /**
   * Turn the radio into a clickable tile. A border and padding is added.
   *
   * **Do not** use interactive elements (links/buttons) inside of the slots when using this prop.
   * An exception is made when using the `tile` + `expand` props together,
   * which allows you to use interactive elements.
   *
   * @category Tile
   */
  @Prop() tile?: boolean = false;
  /**
   * Allow the radio to control the slot area "content".
   * When checked, the area is visible, when unchecked, the area is hidden.
   *
   * The prop `tile` must be used at the same time.
   * @see {@link tile}
   * @since v7.17.0
   * @category Tile
   */
  @Prop() expand?: boolean = false;
  /**
   * Add an icon on the right of your radio tile. The `tile` prop must be `true` for the icon to work.
   * @see {@link tile}
   * @category Tile
   */
  @Prop() icon?: string = null;

  @Watch('radioid')
  handleId() {
    this.idHelpertext = `${this.radioid}-helpertext`;
  }

  @Watch('checked')
  handleChecked() {
    this.checked = this.radioInput.checked;
    if (!this.displayContent()) return;

    if (this.checked) {
      this.openDropdown(this.contentArea);
    } else {
      this.closeDropdown(this.contentArea);
    }
  }

  @Listen('change', { target: 'window' })
  handleChange(e: Event) {
    const { name } = e.target as HTMLInputElement;
    const isSameRadioGroup = name === this.name;

    /**
     * Since content can be nested inside of this component,
     * double check that the event is coming from this specific radio.
     */
    if (isSameRadioGroup) this.checked = this.radioInput.checked;
  }

  componentWillLoad() {
    this.handleId();
  }

  componentDidLoad() {
    requestAnimationFrame(() => {
      if (this.displayContent() && this.radioInput.checked) this.openDropdown(this.contentArea);
      else this.checked = false;
    });
  }

  private isInvalid(): boolean {
    return this.invalid && !this.disabled;
  }

  private displayText(): boolean {
    return this.displayLabel() || this.displayHelpertext();
  }

  private displayLabel(): boolean {
    return !!this.label;
  }

  private displayHelpertext(): boolean {
    return !!(this.helpertext || this.hostElement.querySelector('[slot="helpertext"]')?.textContent);
  }

  private displayIcon(): boolean {
    return this.tile && !!this.icon;
  }

  private displayContent(): boolean {
    return this.tile && this.expand;
  }

  render() {
    return (
      <Host>
        <input
          type="radio"
          id={this.radioid}
          class="pn-radio-input"
          value={this.value}
          name={this.name}
          disabled={this.disabled}
          checked={this.checked}
          required={this.required}
          aria-invalid={this.isInvalid()?.toString()}
          aria-describedby={this.helpertext && this.idHelpertext}
          data-tile={this.tile}
          data-expand={this.expand}
          ref={el => (this.radioInput = el)}
        />
        <div class="pn-radio" data-tile={this.tile} data-expand={this.expand} data-invalid={this.isInvalid()}>
          <div class="pn-radio-button">
            <div class="pn-radio-outer">
              <div class="pn-radio-inner" />
            </div>
          </div>

          <p class="pn-radio-content" hidden={!this.displayText()}>
            <label htmlFor={this.radioid} class="pn-radio-label" hidden={!this.displayLabel()}>
              {this.label}
            </label>
            <span id={this.idHelpertext} class="pn-radio-helpertext" hidden={!this.displayHelpertext()}>
              {this.helpertext}
              <slot name="helpertext"></slot>
            </span>
            <slot></slot>
          </p>

          {this.displayIcon() && <pn-icon icon={this.icon} color="gray900" />}

          <div
            class="pn-radio-container"
            data-open={this.checked}
            hidden={!this.displayContent()}
            style={{ height: '0px' }}
            ref={el => (this.contentArea = el)}
          >
            <div class="pn-radio-area">
              <slot name="content"></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
